import { Injectable, OnDestroy } from '@angular/core';
import { Router, NavigationEnd, NavigationStart, ActivatedRoute, Scroll } from '@angular/router';
import { Observable, BehaviorSubject, Subscription } from 'rxjs';
import { Location } from '@angular/common';
import { ChildActivationEnd } from '@angular/router';
import { ParamMap } from '@angular/router';
import { AmplifyRouteModel } from '../../AmplifyRouter/models/amplify-route.model';
import { AmplifyRouteTypeEnum } from '../../AmplifyRouter/enums/amplify-route-type.enum';
import { Title } from '@angular/platform-browser';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';

@Injectable()
export class AmplifyRoutingService implements OnDestroy {
    // Breaks the URL into known pieces so the current location can be evaluated
    private urlParse: RegExp = /Amplify\/?(wires|editorial)?\/?(wire)?\/?(\d+|new)?/;

    private amplifyRootPath: string = 'Amplify';
    private queryParamMap: ParamMap;
    private currentRoute: AmplifyRouteModel;
    private currentRouteProvider: BehaviorSubject<AmplifyRouteModel>;
    private cachedNavigationStart: NavigationStart;
    private isReload: boolean = true;

    private subscriptions: Subscription[] = [];

    constructor(
        private router: Router,
        private location: Location,
        private titleService: Title,
        private localizationService: LocalizationService,
        private route: ActivatedRoute
    ) {
        this.currentRoute = new AmplifyRouteModel(null);
        this.currentRouteProvider = new BehaviorSubject<AmplifyRouteModel>(this.currentRoute);

        this.subscriptions.push(
            this.router.events.subscribe((e) => {
                if (e instanceof NavigationStart) {
                    this.cachedNavigationStart = e;
                }

                if (e instanceof Scroll && this.isReload) {
                    this.isReload = false;
                    if (e.routerEvent instanceof NavigationEnd) {
                        this.updateCurrentRoute(e.routerEvent.urlAfterRedirects);
                        this.setBrowserTitle();
                    }
                }

                if (e instanceof NavigationEnd) {
                    this.isReload = false;
                    this.updateCurrentRoute(e.urlAfterRedirects);
                    this.setBrowserTitle();
                }
                // child activation end occurs before navigation end
                if (e instanceof ChildActivationEnd) {
                    this.queryParamMap = e.snapshot.queryParamMap;
                }
            }),
        );

        this.initialRouteCheck(this.router.url);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    /**
     * Checks if the initial route will properly load data, and if not, then go to the default route
     * Default route is first topic if it exists, and otherwise search.
     * @param url The current full URL
     */
    private initialRouteCheck(url: string) {
        const matches = url.match(this.urlParse) || [];

        if (!matches) {
            // Not a monitoring route
            return;
        } else {
            switch (matches[1]) {
                case 'wires':
                    this.updateCurrentRoute(url);
                    break;

                case 'editorial':
                    this.updateCurrentRoute(url);
                    break;

                default:
                    // this.gotoDefault();
                    break;
            }
        }
    }

    /**
     * Updates the current route from a URL
     * @param url The current full URL
     */
    private updateCurrentRoute(url: string): void {
        const route = this.getRouteModel(url);

        if (!route) {
            return;
        }

        this.currentRoute = route;

        this.currentRoute.queryParamMap = this.queryParamMap;
        this.currentRouteProvider.next(this.currentRoute);
    }

    private setBrowserTitle() {
        let child = this.route.firstChild;
        while (child.firstChild) {
            child = child.firstChild;
        }
        if (!!child.snapshot.data['title']) {
            this.titleService.setTitle(
                this.localizationService.transform(
                    child.snapshot.data['title']
                )
            );
        }
    }

    public gotoWire(id?: number): void {
        if (id) {
            this.goto(['wires', 'wire', id]);
        } else {
            this.goto(['wires', 'wire' ]);
        }
    }

    public gotoWireReport(id?: number): void {
        this.goto(['wires', 'wire', id, 'report']);
    }

    public gotoWireHype(): void {
        this.goto(['wires']);
    }

    public gotoEditorial(id: number): void {
        this.goto(['editorial', 'wire', id]);
    }

    public gotoEditorialHype(): void {
        this.goto(['editorial']);
    }

    /**
     * Returns a route model based on a url passed in
     *
     * @param url -  url to base route model on
     *
     * @returns MonitoringRouteModel
     */
    getRouteModel(url: string): AmplifyRouteModel {
        const matches = url.match(this.urlParse);
        let route: AmplifyRouteModel;
        if (!matches) {
            return;
        } else {
            switch (matches[1]) {
                case 'wires':
                    if (matches[2]) {
                        const id = matches[3];
                        if (!id) {
                            route = new AmplifyRouteModel(AmplifyRouteTypeEnum.wire);
                            break;
                        }
                        if (matches[4] === 'report') {
                            route = new AmplifyRouteModel(AmplifyRouteTypeEnum.wireReport, id);
                        } else {
                            route = new AmplifyRouteModel(AmplifyRouteTypeEnum.wire, id);
                        }
                    } else {
                        route = new AmplifyRouteModel(AmplifyRouteTypeEnum.wireHype);
                    }
                    break;
                case 'editorial':
                    if (matches[2] && matches[3]) {
                        route = new AmplifyRouteModel(AmplifyRouteTypeEnum.editorial, matches[3]);
                    } else {
                        route = new AmplifyRouteModel(AmplifyRouteTypeEnum.editorialHype);
                    }
                    break;
                default:
                    // Inside amplify, but unrecognized, so go to default
                    route = new AmplifyRouteModel(AmplifyRouteTypeEnum.default);
                    break;
            }
        }

        return route;
    }

    /**
     * Gets an observable for the current route
     *
     * @returns An observable that's updated each time the route changes
     */
    public getCurrentRoute(): Observable<AmplifyRouteModel> {
        return this.currentRouteProvider;
    }

    /**
     * Returns the last cached NavigationStart object
     *
     * @returns NavigationStart
     */
    public getNextRoute(): AmplifyRouteModel {
        return this.getRouteModel(this.cachedNavigationStart.url);
    }

    private goto(path: (string | number)[], replaceURL: boolean = false, queryParams: any = {}): void {
        this.router.navigate(
            [this.amplifyRootPath, ...path],
            // Optionally replace the previous URL in the browser history
            {
                replaceUrl: replaceURL,
                queryParams: queryParams
            }
        );
    }
}

